// src/components/header/article/BtnClient/Btn.tsx
'use client';

import { useEffect, useMemo, useState } from 'react';
import { IconMore, IconBackArrow } from '@/components/icons';
import { useRouter } from 'next/navigation';
import DropDownMenu from '@/components/ui/dropDownMenu/DropDownMenu';
import type { Article } from '@/model/article';
import ShareModal from '@/components/ui/modal/ShareModal';
import ReportModal from '@/components/ui/modal/ReportModal';
import IconModal from '@/components/ui/modal/IconModal';
import { useAppSelector } from '@/store/hook';
import { ArticleService } from '@/libs/services/article';

type MeInfoLike = {
  womo_id?: string;
  womoId?: string;
  user_id?: string | number;
  userId?: string | number;
};

// Header 的返回鍵
// export const ArticleHeaderBackBtn = () => {
//   const router = useRouter();

//   return (
//     <button type="button" onClick={() => router.back()}>
//       <IconBackArrow width={20} />
//     </button>
//   );
// };

export const ArticleHeaderBackBtn = ({ onClose }: { onClose?: () => void }) => {
  const router = useRouter();

  const handleBack = () => {
    if (onClose) {
      // ✅ 如果是在 Modal 裡，執行關閉邏輯
      onClose();
    } else {
      // ✅ 如果是獨立頁面，執行路由返回
      router.back();
    }
  };

  return (
    <button type="button" onClick={handleBack}>
      <IconBackArrow width={20} />
    </button>
  );
};

type ArticleHeaderFuncBtnProps = {
  article?: Article;
  /** 刪除成功時給父層通知（例如 ArticleList 裡直接把該卡片 filter 掉） */
  onDeleted?: (articleId: string) => void;
};

// 幫忙把 `${table}_${table_id}` → { tableId }
function parseTableId(article?: Article): string | null {
  if (!article?.id || !article.table) return null;

  const table = article.table;
  const id = article.id; // 目前是 `${table}_${table_id}`

  const prefix = `${table}_`;
  if (id.startsWith(prefix)) {
    return id.slice(prefix.length);
  }

  const parts = id.split('_');
  return parts[parts.length - 1] || null;
}

// localStorage key：同一篇文章用同一個 key
function getFavStorageKey(article?: Article) {
  if (!article?.id) return null;
  return `fav:${article.id}`;
}

// Header 的功能鍵（更多選單 + 內建 ShareModal）
export const ArticleHeaderFuncBtn = ({ article, onDeleted }: ArticleHeaderFuncBtnProps) => {
  const router = useRouter();
  const meInfo = useAppSelector((state) => state.auth.meInfo) as MeInfoLike | null;

  const [isMounted, setIsMounted] = useState(false);

  // 在客戶端完成掛載後，將狀態設為 true
  useEffect(() => {
    setIsMounted(true);
  }, []);

  const [openShare, setOpenShare] = useState(false);
  const [openReport, setOpenReport] = useState(false);

  // 刪除確認 Modal
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
  // 刪除成功的通用 Modal
  const [modalType, setModalType] = useState<'success' | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // 刪除後要做什麼動作
  const [afterDeleteAction, setAfterDeleteAction] = useState<
    'detail-navigate' | 'list-refresh' | null
  >(null);

  const [shareLoading, setShareLoading] = useState(false);

  // ========= 分享用 URL =========
  const shareUrl = useMemo(() => {
    if (typeof window !== 'undefined') {
      if (article?.id) {
        // 依照當前 origin 組絕對網址（local / prod 都 OK）
        return new URL(`/article/${article.id}`, window.location.origin).toString();
      }
      return window.location.href;
    }
    // SSR fallback：給個正式網域（請依你的正式 domain 調整）
    return article?.id ? `https://www.womo.com.tw/article/${article.id}` : '';
  }, [article?.id]);

  // ========= 分享用 Title =========
  const shareTitle = useMemo(() => {
    if (article?.title && article.title.trim()) return article.title.trim();
    if (article?.author?.name && article.author.name.trim()) {
      return `${article.author.name.trim()} 的籌碼心情`;
    }
    return 'WOMO 的籌碼心情';
  }, [article?.title, article?.author?.name]);

  // ========= 分享用 Description =========
  const shareDesc = useMemo(() => {
    if (!article?.content) return '';
    return article.content
      .replace(/<[^>]+>/g, ' ') // 去掉 HTML tag
      .replace(/\s+/g, ' ')
      .slice(0, 160);
  }, [article?.content]);

  // ========= 編輯權限：本人 + API is_edit =========
  const canEdit = !!meInfo && !!article?.isEdit;

  const tableId = parseTableId(article);
  const table = article?.table;
  const favStorageKey = getFavStorageKey(article);

  // ========= 蒐藏狀態：先看 localStorage，再 fallback 到 Article，再由 API 覆寫 =========
  const [isFav, setIsFav] = useState<boolean>(() => {
    // 這段在 SSR 也會跑，所以要防守 window
    if (typeof window !== 'undefined' && favStorageKey) {
      const saved = window.localStorage.getItem(favStorageKey);
      if (saved === '1') return true;
      if (saved === '0') return false;
    }
    return article?.isFav ?? false;
  });

  const [favLoading, setFavLoading] = useState(false);

  // ⭐ 進入頁面（或換文章 / 換登入者）時，重新從 /article/fav 抓一次「真實收藏狀態」
  useEffect(() => {
    if (!article || !table || !tableId) return;

    // 先套用 localStorage 的狀態（避免剛收藏完立刻重整會看到舊資料）
    if (typeof window !== 'undefined' && favStorageKey) {
      const saved = window.localStorage.getItem(favStorageKey);
      if (saved === '1') {
        setIsFav(true);
      } else if (saved === '0') {
        setIsFav(false);
      } else {
        // localStorage 沒紀錄 → 用文章原本的 isFav
        setIsFav(article.isFav ?? false);
      }
    } else {
      setIsFav(article.isFav ?? false);
    }

    // 沒登入就不打個人化 /fav
    if (!meInfo) return;

    let cancelled = false;

    const fetchFav = async () => {
      try {
        const { isFav: remoteIsFav } = await ArticleService.fav({
          table,
          table_id: tableId,
        });

        if (cancelled) return;

        const finalIsFav = remoteIsFav ?? article.isFav ?? false;

        setIsFav(finalIsFav);

        if (typeof window !== 'undefined' && favStorageKey) {
          window.localStorage.setItem(favStorageKey, finalIsFav ? '1' : '0');
        }
      } catch (err) {
        console.error('[ArticleHeaderFuncBtn] preload fav error:', err);
        if (!cancelled) {
          // 發生錯誤就回退到 props 狀態
          setIsFav(article.isFav ?? false);
        }
      }
    };

    void fetchFav();

    return () => {
      cancelled = true;
    };
    // 依賴：文章 / table / tableId / 登入者 / localStorage key
  }, [article, table, tableId, meInfo, favStorageKey]);

  // ========= 編輯 =========
  const handleEdit = () => {
    if (!article) return;

    // /article/edit/[articleId]，article.id = 'collection_714' / 'store_article_417' ...
    // router.push(`/article/edit/${article.id}`);
    router.push(`/me/article/${table}/${tableId}/edit`); //<<<皇冠暫時改，請小雯再調整"
  };

  // ========= 刪除：先打開確認 IconModal（warning） =========
  const handleDelete = () => {
    if (!article) return;

    // 目前後端只提供 DELETE /article/collection/{id}
    if (article.table !== 'collection') {
      alert('目前僅支援刪除心情貼文（collection）。');
      return;
    }

    setOpenDeleteConfirm(true);
  };

  // ========= 確認刪除：真正打 API =========
  const handleConfirmDelete = async () => {
    if (!article) return;
    if (deleteLoading) return;

    const table = article.table;
    const id = parseTableId(article);

    if (!table || !id) {
      alert('無法取得文章資訊，刪除失敗');
      return;
    }

    try {
      setDeleteLoading(true);

      await ArticleService.deleteArticle({
        table,
        table_id: id,
      });

      // ---------------------------
      // 刪除成功後的行為：
      // 1. 如果父層有傳 onDeleted => 通知父層處理（不做導頁 / refresh）
      // 2. 否則依照目前路徑決定 detail-navigate / list-refresh
      // ---------------------------
      let action: 'detail-navigate' | 'list-refresh' | null = null;

      if (onDeleted && article.id) {
        onDeleted(article.id);
        action = null; // 完全交給父層處理，不做導頁/refresh
      } else if (typeof window !== 'undefined') {
        const path = window.location.pathname;

        // e.g. /article/collection_714 → 視為 detail 頁
        if (/^\/article\/[^/]+/.test(path)) {
          action = 'detail-navigate';
        } else {
          // /article（列表）或其他嵌入 ArticleList 的頁面
          action = 'list-refresh';
        }
      }

      setAfterDeleteAction(action);
      setOpenDeleteConfirm(false);

      // 顯示刪除成功的通用 IconModal（2 秒自動關閉）
      setModalType('success');
    } catch (err) {
      console.error('[ArticleHeaderFuncBtn] delete article error:', err);
      alert('刪除失敗，請稍後再試');
    } finally {
      setDeleteLoading(false);
    }
  };

  // ========= 蒐藏 / 取消蒐藏：串 /article/fav =========
  const handleToggleFav = async () => {
    if (!article || !table || !tableId) return;
    if (favLoading) return;

    // ⭐ 樂觀更新：先讓 UI 切換 + localStorage 更新
    setIsFav((prev) => {
      const next = !prev;
      if (typeof window !== 'undefined' && favStorageKey) {
        window.localStorage.setItem(favStorageKey, next ? '1' : '0');
      }
      return next;
    });

    try {
      setFavLoading(true);

      const { isFav: nextIsFav } = await ArticleService.fav({
        table,
        table_id: tableId,
      });

      console.log('[ArticleHeaderFuncBtn] fav response:', { nextIsFav });

      setIsFav(nextIsFav);

      if (typeof window !== 'undefined' && favStorageKey) {
        window.localStorage.setItem(favStorageKey, nextIsFav ? '1' : '0');
      }
    } catch (err) {
      console.error('[ArticleHeaderFuncBtn] toggle fav error:', err);

      // 若失敗，就把 UI 切回來 + localStorage 一起反轉回去
      setIsFav((prev) => {
        const reverted = !prev;
        if (typeof window !== 'undefined' && favStorageKey) {
          window.localStorage.setItem(favStorageKey, reverted ? '1' : '0');
        }
        return reverted;
      });

      alert('蒐藏操作失敗，請稍後再試');
    } finally {
      setFavLoading(false);
    }
  };

  // ========= 分享：串 /article/share，並通知 Detail 把 shareCount +1 =========
  const handleShare = async () => {
    // 若正在分享中，避免連點
    if (shareLoading) return;

    // 若沒有 table 或 tableId，就只打開分享 Modal
    if (!table || !tableId) {
      setOpenShare(true);
      return;
    }

    try {
      setShareLoading(true);

      await ArticleService.share({
        table,
        table_id: tableId,
      });

      if (typeof window !== 'undefined' && article?.id) {
        // 通知 ArticleDetail 把 shareCount +1
        window.dispatchEvent(
          new CustomEvent('article-share', {
            detail: { id: article.id },
          })
        );
      }
    } catch (err) {
      console.error('[ArticleHeaderFuncBtn] share error:', err);
      // 這邊先不強制 alert，避免干擾使用者；需要可以再加
    } finally {
      setShareLoading(false);
      // 無論成功或失敗，都可以打開自家分享 Modal
      setOpenShare(true);
    }
  };

  // ========= 文章擁有者的 kol_id：給 DropDownMenu 判斷「本人」 =========
  // 在 normalizeArticle 裡，我們已經讓 author.id 優先吃 kol_id
  const ownerKolId = article?.author?.id;

  // ========= 刪除成功後，成功 modal 關閉時要做的導頁/刷新 =========
  const handleAfterDelete = () => {
    const action = afterDeleteAction;

    setModalType(null);
    setAfterDeleteAction(null);

    if (!action) return;

    if (action === 'detail-navigate') {
      // detail 頁：回上一頁，或退回 /article
      if (typeof window !== 'undefined' && window.history.length > 1) {
        router.back();
      } else {
        router.replace('/article');
      }
    } else if (action === 'list-refresh') {
      // 列表 / 其它嵌入頁：保留當前路徑，重新抓資料
      if (typeof window !== 'undefined') {
        const r = router as unknown as { refresh?: () => void };

        if (typeof r.refresh === 'function') {
          r.refresh();
        } else {
          window.location.reload();
        }
      }
    }
  };

  return (
    <>
      {/* 只有在客戶端掛載後才渲染 DropDownMenu */}
      {isMounted && (
        <DropDownMenu
          trigger={
            <button type="button" className="ms-5" disabled={favLoading || shareLoading}>
              <IconMore width={24} className="text-grey" />
            </button>
          }
          // 判斷是否本人貼文
          ownerKolId={ownerKolId}
          // 判斷是否能編輯（is_edit + 有登入）
          canEdit={canEdit}
          // 判斷蒐藏 icon / 文案
          isFav={isFav}
          // 蒐藏：實際串 API（DropDownMenu 只負責 requireLogin + 顯示 icon）
          onFavor={handleToggleFav}
          // 編輯 / 刪除：本人 + canEdit 才會顯示
          onEdit={handleEdit}
          onDelete={handleDelete}
          // 點「分享」→ 打開內建 ShareModal
          onShare={handleShare}
          onReport={() => setOpenReport(true)}
        />
      )}

      {/* 自家分享 Modal：把 url / title / desc 都丟進去 */}
      <ShareModal
        open={openShare}
        onClose={() => setOpenShare(false)}
        title="分享貼文"
        url={shareUrl || undefined}
        shareTitle={shareTitle}
        shareDescription={shareDesc || undefined}
      />

      <ReportModal open={openReport} onClose={() => setOpenReport(false)} />

      {/* 刪除確認的提示 IconModal（warning） */}
      <IconModal
        show={openDeleteConfirm}
        variant="warning"
        size="md"
        title="是否要刪除本篇文章？"
        onClose={() => {
          if (deleteLoading) return;
          setOpenDeleteConfirm(false);
        }}
        onConfirm={handleConfirmDelete}
      >
        刪除後即無法恢復動作。
      </IconModal>

      {/* 刪除成功的提示 IconModal（通用 success modal） */}
      <IconModal
        show={modalType === 'success'}
        variant="success"
        size="md"
        title="已成功刪除文章！"
        autoCloseSeconds={2}
        onClose={handleAfterDelete}
      />
    </>
  );
};
